import { XY } from './V2.js';

import { Rndm } from './Rndm.js';
import { Arry } from './Arry.js';
import { Ease } from './Ease.js';
import { loop_type } from './Loop.js';

export interface pnts_manager_type {
    list: pnts_type[];
    kill: (id: string) => void;
    get: (id: string) => pnts_type | false;
    run: (time?: loop_type) => void;
    make: (io?: pnts_input_type) => pnts_type;
}

export interface pnts_type {
    id: string;
    type: string,
    drag: number;
    mass: number;
    pos: XY;
    dif: XY;
    acc: XY;
    start: XY;
    kill: () => void;
}

interface pnts_input_type {
    id?: string;
    drag?: number;
    mass?: number;
    x?: number;
    y?: number;
    fx?: number;
    fy?: number;
    dx?: number;
    dy?: number;
    pos?: XY;
    dif?: XY;
    acc?: XY;
    [key: string]: any;
}

export function outside_box(p: XY, width = 0, height = 0, box: any, padding = 0.1): XY {
    const over = { x: 0, y: 0, out: false };
    if (p.pos.x - width < box.x + padding) {
        over.x = p.pos.x - width - box.x - padding;
        over.out = true;
    }
    if (p.pos.x + width > box.xx - padding) {
        over.x = p.pos.x + width - box.xx + padding;
        over.out = true;
    }
    if (p.pos.y - height < box.y + padding) {
        over.y = p.pos.y - height - box.y - padding;
        over.out = true;
    }
    if (p.pos.y + height > box.yy - padding) {
        over.y = p.pos.y + height - box.yy + padding;
        over.out = true;
    }
    return over; //gibt zurück ob und wie weit ausserhalb {x: 0.00164, y: 0.00344, out: true}
}

type line_hit = {
    point_index: number;
    distance: number;
    segment: number;
    normal: XY;
};

export function apply_line_push(line_a: pnts_type[], line_b: pnts_type[], hits: line_hit[], padding: number, amp = 0.1, force_share = 0.5, ease: keyof typeof Ease = "sineIn", pos_share = 0.5) { //force_share: how push is split between line_a point and line_b segment endpoints; pos_share: 0 = pure velocity push, 1 = pure positional correction (PBD-style, kills resting jitter)
    const ease_fn = Ease[ease] as (t: number) => number;
    for (const h of hits) {
        const t = 1 - h.distance / padding; //0 at padding edge, 1 at full overlap
        const eased = ease_fn(t);
        const push = eased * padding * amp;
        const px = h.normal.x * push;
        const py = h.normal.y * push;
        const pa = line_a[h.point_index];
        const a = line_b[h.segment];
        const b = line_b[(h.segment + 1) % line_b.length];
        const v = 1 - pos_share;
        pa.dif.x += px * (1 - force_share) * v;
        pa.dif.y += py * (1 - force_share) * v;
        a.dif.x -= px * force_share * 0.5 * v;
        a.dif.y -= py * force_share * 0.5 * v;
        b.dif.x -= px * force_share * 0.5 * v;
        b.dif.y -= py * force_share * 0.5 * v;
        if (pos_share > 0) {
            pa.pos.x += px * (1 - force_share) * pos_share;
            pa.pos.y += py * (1 - force_share) * pos_share;
            a.pos.x -= px * force_share * 0.5 * pos_share;
            a.pos.y -= py * force_share * 0.5 * pos_share;
            b.pos.x -= px * force_share * 0.5 * pos_share;
            b.pos.y -= py * force_share * 0.5 * pos_share;
        }
    }
}

export function pnts_manager() {

    const list: pnts_type[] = [];

    const self = {
        list,
        kill: (id: string) => {
            Arry.remove.id(list, id);
        },
        get: (id: string) => {
            return Arry.get.id(list, id) as pnts_type | false;
        },
        run: function (time?: loop_type): void {
            const step = time ? (time.delta_ms * 0.05) : 1;
            for (const p of list) {
                p.dif.x += p.acc.x * p.mass;
                p.dif.y += p.acc.y * p.mass;
                p.pos.x += p.dif.x * step;
                p.pos.y += p.dif.y * step;
                p.dif.x *= p.drag;
                p.dif.y *= p.drag;
            }
        },
        make: function (io: pnts_input_type = {}): pnts_type {

            const id = io.id || Rndm.id();

            // Create start pos based on input or random
            const startX = io.x ?? io.pos?.x ?? Rndm.float();
            const startY = io.y ?? io.pos?.y ?? Rndm.float();

            const point: pnts_type = {
                ...io,
                id,
                type: "pnts",
                drag: io.drag || 0.94,
                mass: io.mass ?? 1,
                pos: { x: startX, y: startY },
                dif: { x: io.dx ?? 0, y: io.dy ?? 0 },
                acc: { x: io.fx ?? 0, y: io.fy ?? 0 },
                start: { x: startX, y: startY },
                kill: () => Arry.remove.id(list, id)
            };

            list.push(point);
            return point;
        }
    };
    return self;
}